import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, checkAuth, api } = useAuth();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setBio(user.bio || "");
      setAvatar(user.avatar || "");
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/users/me/profile", { name, bio, avatar });
      await checkAuth();
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 pt-24 md:pt-28 pb-24" data-testid="profile-page">
      <div className="max-w-xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Account</p>
          <h1 className="font-serif text-4xl font-bold text-slate-900 tracking-tight">Profile</h1>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 space-y-6">
          {/* Avatar preview */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 flex items-center justify-center text-white text-xl font-bold font-serif overflow-hidden">
              {avatar ? (
                <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                name?.[0]?.toUpperCase() || "U"
              )}
            </div>
            <div>
              <p className="font-sans text-lg font-medium text-slate-900">{name || "Your Name"}</p>
              <p className="font-mono text-xs text-slate-400">{user?.email}</p>
              <p className="font-mono text-xs text-slate-400 mt-0.5 capitalize">Role: {user?.role}</p>
            </div>
          </div>

          <div className="section-gradient-line"></div>

          <div className="space-y-4">
            <div>
              <Label className="font-sans text-sm">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="profile-name-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Bio</Label>
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell us about yourself..." className="mt-1 min-h-[80px]" data-testid="profile-bio-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Avatar URL</Label>
              <Input value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="https://..." className="mt-1" data-testid="profile-avatar-input" />
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans"
            data-testid="profile-save-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </main>
  );
}
